import { supabase } from '../../config/supabase';
import type {
  Concept2ConnectionStatus,
  Concept2Workout,
  Concept2WorkoutFilters,
  Concept2WorkoutWithSegments,
} from '../../types/concept2';

const API_ERROR_MESSAGES: Record<string, string> = {
  concept2_unauthorized: 'Sign in as an athlete to manage your Concept2 Logbook.',
  concept2_rower_required: 'Concept2 Logbook is available to rower accounts only.',
  concept2_not_connected: 'Connect your Concept2 Logbook first.',
  concept2_confirmation_required: 'Type REMOVE_CONCEPT2_DATA to confirm removal.',
  concept2_sync_unavailable: 'Sync is temporarily unavailable. Try again shortly.',
  concept2_disconnect_unavailable: 'Disconnect is temporarily unavailable. Try again shortly.',
  concept2_remove_unavailable: 'Remove data is temporarily unavailable. Try again shortly.',
  concept2_status_unavailable: 'Connection status is temporarily unavailable.',
  concept2_config_invalid: 'Concept2 is not configured for this environment.',
  concept2_state_unavailable: 'Could not start Concept2 authorization. Try again shortly.',
};

export const CONCEPT2_OAUTH_REASON_MESSAGES: Record<string, string> = {
  account_already_linked: 'This Concept2 account is already linked to another Glyyd rower.',
  state_expired: 'The connection request expired. Try connecting again.',
  vendor_unavailable: 'Concept2 is temporarily unavailable. Try again later.',
  sync_enqueue_failed: 'Concept2 connected, but the initial import could not start. Try Sync now.',
  invalid_request: 'The Concept2 connection request was invalid. Try again.',
};

async function getAuthHeaders(): Promise<Record<string, string>> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  if (!token) {
    throw new Error('concept2_unauthorized');
  }
  return {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
}

function getApiBaseUrl(): string {
  const isLocalhost =
    typeof window !== 'undefined' &&
    (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
  return isLocalhost ? 'http://localhost:3001' : '';
}

export function mapConcept2ApiError(code: string | undefined, fallback = 'Request failed'): string {
  if (code && API_ERROR_MESSAGES[code]) {
    return API_ERROR_MESSAGES[code];
  }
  return fallback;
}

async function apiRequest<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = await getAuthHeaders();
  const apiBase = getApiBaseUrl();
  const url = apiBase ? `${apiBase}${path}` : path;
  const response = await fetch(url, {
    ...init,
    headers: {
      ...headers,
      ...(init.headers || {}),
    },
  });

  const contentType = response.headers.get('content-type');
  const body =
    contentType && contentType.includes('application/json') ? await response.json() : null;

  if (!response.ok) {
    const code = typeof body?.error === 'string' ? body.error : undefined;
    throw new Error(code || `concept2_http_${response.status}`);
  }

  return body as T;
}

class Concept2Service {
  async getStatus(): Promise<Concept2ConnectionStatus> {
    return apiRequest<Concept2ConnectionStatus>('/api/concept2/status', { method: 'GET' });
  }

  async connect(): Promise<{ authorizationUrl: string }> {
    return apiRequest<{ authorizationUrl: string }>('/api/concept2/connect', { method: 'POST' });
  }

  async sync(): Promise<{ queued: boolean; jobId: string }> {
    return apiRequest<{ queued: boolean; jobId: string }>('/api/concept2/sync', {
      method: 'POST',
    });
  }

  async processJobs(): Promise<{ success: boolean }> {
    return apiRequest<{ success: boolean }>('/api/concept2/process-jobs', {
      method: 'POST',
    });
  }

  async disconnect(): Promise<{ disconnected: boolean }> {
    return apiRequest<{ disconnected: boolean }>('/api/concept2/disconnect', {
      method: 'POST',
    });
  }

  async removeData(): Promise<{ removed: boolean }> {
    return apiRequest<{ removed: boolean }>('/api/concept2/remove-data', {
      method: 'DELETE',
      body: JSON.stringify({ confirmation: 'REMOVE_CONCEPT2_DATA' }),
    });
  }

  async listWorkoutsForRower(
    rowerProfileId: string,
    filters: Concept2WorkoutFilters = {}
  ): Promise<Concept2Workout[]> {
    let query = supabase
      .from('concept2_workouts')
      .select('*')
      .eq('rower_profile_id', rowerProfileId)
      .is('vendor_deleted_at', null);

    if (filters.machineType) {
      query = query.eq('machine_type', filters.machineType);
    }
    if (filters.dateFrom) {
      query = query.gte('workout_local_at', filters.dateFrom);
    }
    if (filters.dateTo) {
      query = query.lte('workout_local_at', filters.dateTo);
    }

    const { data, error } = await query.order('workout_local_at', { ascending: false });
    if (error) {
      throw new Error('concept2_workouts_unavailable');
    }
    return (data || []) as unknown as Concept2Workout[];
  }

  async listWorkouts(filters: Concept2WorkoutFilters = {}): Promise<Concept2Workout[]> {
    let query = supabase.from('concept2_workouts').select('*').is('vendor_deleted_at', null);

    if (filters.machineType) {
      query = query.eq('machine_type', filters.machineType);
    }
    if (filters.dateFrom) {
      query = query.gte('workout_local_at', filters.dateFrom);
    }
    if (filters.dateTo) {
      query = query.lte('workout_local_at', filters.dateTo);
    }

    const { data, error } = await query.order('workout_local_at', { ascending: false });
    if (error) {
      throw new Error('concept2_workouts_unavailable');
    }
    return (data || []) as unknown as Concept2Workout[];
  }

  async getWorkout(workoutId: string): Promise<Concept2WorkoutWithSegments | null> {
    const { data: workout, error: workoutError } = await supabase
      .from('concept2_workouts')
      .select('*')
      .eq('id', workoutId)
      .is('vendor_deleted_at', null)
      .maybeSingle();

    if (workoutError) {
      throw new Error('concept2_workout_unavailable');
    }
    if (!workout) {
      return null;
    }

    const { data: segments, error: segmentError } = await supabase
      .from('concept2_workout_segments')
      .select('*')
      .eq('workout_id', workoutId)
      .order('sequence', { ascending: true });

    if (segmentError) {
      throw new Error('concept2_workout_unavailable');
    }

    const { data: strokes, error: strokeError } = await supabase
      .from('concept2_workout_strokes')
      .select(
        'stroke_index, incremental_time_tenths, workout_time_tenths, incremental_distance_decimeters, pace_tenths, stroke_rate, heart_rate'
      )
      .eq('workout_id', workoutId)
      .order('stroke_index', { ascending: true });

    if (strokeError) {
      throw new Error('concept2_workout_unavailable');
    }

    const { data: metadata, error: metadataError } = await supabase
      .from('concept2_workout_metadata')
      .select('*')
      .eq('workout_id', workoutId)
      .maybeSingle();

    if (metadataError) {
      throw new Error('concept2_workout_unavailable');
    }

    return {
      ...(workout as unknown as Concept2Workout),
      segments: (segments || []) as unknown as Concept2WorkoutWithSegments['segments'],
      strokes: (strokes || []) as unknown as Concept2WorkoutWithSegments['strokes'],
      metadata: (metadata || null) as unknown as Concept2WorkoutWithSegments['metadata'],
    };
  }
}

export const concept2Service = new Concept2Service();
